/**
 * CREATIVE DETONATOR - Dev Mode Module
 * Development and testing utilities
 *
 * Features:
 * - Clickable phase dots for jumping between phases
 * - Dummy data generation for testing UI
 * - Pre-populated brief and trigger data
 * - SIMULATION MODE for Phase 3 (no API calls)
 */

// v61 - Added simulation mode support
import { GeneratorState, elements } from '../core/state.js';
import { goToPhase, goToPhaseDirectly } from '../core/navigation.js';
import { updateBriefSummary } from '../phases/phase1-ignition.js';
import { populateDummyData, getDummyBrief } from '../core/dummy-data.js';

// Access DataStore from global scope (defined in main.js)
const DataStore = window.DataStore;

// ============================================================================
// CLICKABLE PHASE NAVIGATION
// ============================================================================

/**
 * Initialize clickable phase dots for dev/testing
 * Allows developers to jump directly to any phase
 */
export function initClickablePhases() {
  // Guard: elements may not be populated yet
  if (!elements.phaseSteps) {
    console.warn('[DevMode] phaseSteps not found, skipping clickable phases');
    return;
  }

  // Make phase step buttons clickable for dev/testing
  elements.phaseSteps.forEach(step => {
    step.addEventListener('click', () => {
      const phaseNumber = parseInt(step.dataset.phase);
      jumpToPhase(phaseNumber);
    });
  });
}

/**
 * Jump to a specific phase with auto-populated data if needed
 *
 * SIMULATION MODE:
 * When jumping to Phase 3 via dev mode, we set simulationMode = true.
 * This tells runDetonation() to run the simulation instead of real API calls.
 *
 * @param {number} phaseNumber - Target phase number (1-4)
 */
export function jumpToPhase(phaseNumber) {
  // Pre-populate with dummy data if needed for preview
  if (!GeneratorState.brief.product) {
    const dummyBrief = getDummyBrief();
    GeneratorState.brief = {
      product: dummyBrief.product,
      benefit: dummyBrief.benefit,
      formats: ['video', 'social']  // Use subset for Phase 2 display
    };
    // Update UI to reflect dummy data
    if (elements.inputProduct) elements.inputProduct.value = GeneratorState.brief.product;
    if (elements.inputBenefit) elements.inputBenefit.value = GeneratorState.brief.benefit;
    // Mark format cards as selected
    if (elements.formatCards) {
      elements.formatCards.forEach(card => {
        if (GeneratorState.brief.formats.includes(card.dataset.format)) {
          card.classList.add('selected');
        }
      });
    }
    updateBriefSummary();
  }

  // Pre-select some triggers if going to phase 3 or 4 and none selected
  if ((phaseNumber >= 3) && GeneratorState.selectedTriggers.length === 0) {
    const triggers = DataStore.triggers;
    if (triggers && triggers.length > 0) {
      const selected = triggers.slice(0, 3).map(t => t.code);
      GeneratorState.selectedTriggers = selected;
    }
  }

  // ========================================================================
  // PHASE 4: Direct jump with pre-populated dummy data
  // ========================================================================
  if (phaseNumber === 4 && GeneratorState.generatedIdeas.length === 0) {
    populateDummyData();
    goToPhaseDirectly(4);
    return;
  }

  // ========================================================================
  // PHASE 3: SIMULATION MODE - No API calls
  // ========================================================================
  if (phaseNumber === 3) {
    console.log('[DevMode] Enabling SIMULATION MODE for Phase 3');
    GeneratorState.simulationMode = true;

    // Use goToPhase to trigger the init function (runDetonation)
    // runDetonation will check simulationMode and run simulation instead
    goToPhase(3);
    return;
  }

  // ========================================================================
  // PHASE 1 or 2: Direct navigation
  // ========================================================================
  GeneratorState.simulationMode = false;  // Reset flag
  goToPhaseDirectly(phaseNumber);
}

// ============================================================================
// DUMMY DATA GENERATION (Re-export for backward compatibility)
// ============================================================================

/**
 * Generate dummy ideas for testing Phase 4 UI
 * @deprecated Use populateDummyData() from dummy-data.js instead
 */
export function generateDummyIdeas() {
  populateDummyData();
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility
window.initClickablePhases = initClickablePhases;
window.jumpToPhase = jumpToPhase;
window.generateDummyIdeas = generateDummyIdeas;
